// src/pages/portal/PortalDashboard.js — The portal home (Travis, 10/21).
// Your requests (each led by its mandate, with RGA's answers in the last 30
// days folded in as updates; New since their last visit, counted on the
// Dashboard nav item), then a row of
// tiles for the main places a partner goes (Travis, 10/28): Contacts,
// Submit Names, Submit a Deal; then the partner's engaged mandates and the
// other RGA active mandates as face tiles (same as the Mandates page). The partner's record moved to
// Settings (components/portal/PartnerRecord.js). All of it from GET
// /api/portal/dashboard, the partner's own data only.
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import PortalSearch from '../../components/portal/PortalSearch'
import { Link, useNavigate } from 'react-router-dom'
import api from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { PORTAL_PAGE_TITLE_STYLE, PORTAL_TITLE_ALIGN } from '../../lib/portalPageTitleStyle'
import { BranchedGrid, SECTION_HEADING_STYLE } from './PortalMandates'

const formatDate = d => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—')

function Card({ title, action, children }) {
  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600 }}>{title}</h2>
        {action}
      </div>
      <div style={{ padding: '12px 20px 16px' }}>{children}</div>
    </section>
  )
}

const Muted = ({ children }) => <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{children}</div>

const ico = d => (
  <svg width="62" height="62" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
)
const TILE_ICONS = {
  contacts: ico(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>),
  names: ico(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M16 11h6"/></>),
  // The Mandates briefcase with a plus beside it, like Submit Names.
  deal: ico(<><g transform="translate(0 3) scale(0.75)" strokeWidth="1.87">
    <path d="M20 7h-3V5a2 2 0 0 0-2-2h-6a2 2 0 0 0-2 2v2H4a1 1 0 0 0-1 1v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a1 1 0 0 0-1-1z"/><path d="M9 7V5h6v2"/><path d="M3 12h18"/>
  </g><path d="M20 5v6M17 8h6"/></>),
}

const tileStyle = hover => ({
  aspectRatio: '1 / 1', width: '100%', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', gap: 12, padding: 10,
  background: hover ? 'var(--surface-hover)' : 'var(--surface)', border: '1px solid',
  borderColor: hover ? 'var(--accent)' : 'var(--border)', borderRadius: 'var(--radius, 10px)',
  color: 'var(--text-primary)', textDecoration: 'none', cursor: 'pointer', font: 'inherit',
  transition: 'background 0.15s, border-color 0.15s',
})

function TileFace({ icon, title }) {
  return (
    <>
      <span style={{ color: 'var(--accent)' }}>{icon}</span>
      <span style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.2, textAlign: 'center' }}>{title}</span>
    </>
  )
}

function LinkTile({ to, icon, title }) {
  const [hover, setHover] = useState(false)
  return (
    <Link to={to} style={tileStyle(hover)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <TileFace icon={icon} title={title} />
    </Link>
  )
}

// Names are submitted per mandate, so this tile asks which one first
// (straight through when the partner is engaged on only one).
function SubmitNamesTile({ mandates }) {
  const [hover, setHover] = useState(false)
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return
    const away = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  function onClick() {
    if (mandates.length === 1) navigate(`/portal/mandates/${mandates[0].mandate_id}/add-names`)
    else setOpen(o => !o)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={onClick} style={tileStyle(hover || open)}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
        <TileFace icon={TILE_ICONS.names} title="Submit Names" />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 240, zIndex: 20,
          background: 'var(--surface-raised)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: '6px 0',
        }}>
          <div style={{ padding: '4px 14px 6px', fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>For which mandate?</div>
          {!mandates.length && <div style={{ padding: '6px 14px', fontSize: 13, color: 'var(--text-muted)' }}>You are not engaged on a mandate yet.</div>}
          {mandates.map(m => (
            <Link key={m.mandate_id} to={`/portal/mandates/${m.mandate_id}/add-names`}
              style={{ display: 'block', padding: '7px 14px', fontSize: 13, color: 'var(--text-primary)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
              {m.mandate_name}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

// The partner's engaged mandates, then the other RGA active mandates, as the
// same face tiles and sections as the Mandates page.
function DashboardMandates() {
  const { profile } = useAuth()
  const { data } = useQuery({
    queryKey: ['portalMandates'],
    queryFn: () => api.get('/api/portal/mandates').then(r => r.data.data),
  })
  const engaged = (data || []).filter(m => m.engaged)
  const others = (data || []).filter(m => !m.engaged)
  return (
    <>
      {engaged.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2 style={SECTION_HEADING_STYLE}>{profile?.name ? `${profile.name}'s Mandates` : 'Your Mandates'}</h2>
          <BranchedGrid mandates={engaged} />
        </div>
      )}
      {others.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2 style={SECTION_HEADING_STYLE}>{engaged.length > 0 ? 'Other RGA Active Mandates' : 'Active Mandates'}</h2>
          <BranchedGrid mandates={others} />
        </div>
      )}
    </>
  )
}

function QuickTiles({ mandates }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, 175px)', justifyContent: 'center', alignContent: 'center', gap: 18, height: '100%' }}>
      <LinkTile to="/portal/contacts" icon={TILE_ICONS.contacts} title="Contacts" />
      <SubmitNamesTile mandates={mandates} />
      <LinkTile to="/portal/submit-deal" icon={TILE_ICONS.deal} title="Submit a Deal" />
    </div>
  )
}

// Shared with the nav badge (PortalLayout), so one fetch serves both.
export function usePortalDashboard(options = {}) {
  return useQuery({
    queryKey: ['portalDashboard'],
    queryFn: () => api.get('/api/portal/dashboard').then(r => r.data.data),
    ...options,
  })
}

// Request updates newer than the partner's last dashboard visit.
export function newAlertCount(data) {
  return data && !data.alerts_seen ? (data.requests ?? []).filter(r => r.new).length : 0
}

// One request: mandate (or a deal's company) first and largest, then what
// was asked, then RGA's answer as its update. Status on the right.
function RequestRow({ request: r, first }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
      padding: '12px 0', borderTop: first ? 'none' : '1px solid var(--border)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>
          {r.mandate_id ? <Link to={`/portal/mandates/${r.mandate_id}`} style={{ color: 'inherit' }}>{r.title}</Link> : r.title}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {r.kind}{r.detail ? `: ${r.detail}` : ''}
        </div>
        {r.update && (
          <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            {r.new && <span className="badge badge-blue" style={{ fontSize: 10 }}>New</span>}
            {r.update}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <span className={`badge ${r.open ? 'badge-yellow' : 'badge-green'}`} style={{ fontSize: 11 }}>
          {r.open ? 'Waiting on RGA' : 'Answered'}
        </span>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>{formatDate(r.at)}</div>
      </div>
    </div>
  )
}

export default function PortalDashboard() {
  const { profile } = useAuth()
  const qc = useQueryClient()
  const { data, isLoading, isError } = usePortalDashboard()

  // Opening the dashboard marks alerts seen (migrations/083). The New
  // markers stay for this visit; the nav badge clears now.
  const hasNew = data?.requests?.some(r => r.new && !data.alerts_seen)
  useEffect(() => {
    if (!hasNew) return
    api.post('/api/portal/alerts/seen').then(() => {
      qc.setQueryData(['portalDashboard'], d => d && { ...d, alerts_seen: true })
    }).catch(() => {})
  }, [hasNew, qc])

  if (isError) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>The dashboard could not load</h3>
          <p>Try again in a moment. <Link to="/portal/mandates">Go to Mandates</Link></p>
        </div>
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner spinner-lg" /></div>
      </div>
    )
  }

  const { requests = [], mandates } = data

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text" style={{ flex: 1 }}>
          <h1 style={PORTAL_PAGE_TITLE_STYLE}>Dashboard</h1>
          {profile?.name && <p style={{ textAlign: PORTAL_TITLE_ALIGN }}>Welcome, {profile.name}.</p>}
        </div>
        <PortalSearch />
      </div>

      {/* Squares on the left half; Your requests on the right half (Travis,
          10/28; alerts folded into requests 11/3). Stacks on a narrow screen. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 480px), 1fr))', gap: 24, alignItems: 'stretch' }}>
        <QuickTiles mandates={mandates} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="Your requests">
            {!requests.length ? <Muted>No open requests.</Muted> : requests.map((r, i) => (
              <RequestRow key={r.key} request={r} first={i === 0} />
            ))}
          </Card>
        </div>
      </div>

      <DashboardMandates />
    </div>
  )
}
